// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";

// ----------------------------------------------------------------------- imports internos
import { linkDelQr, qrEstado, qrNuevo } from "../../services/acceso";

/* El QR de la pantalla de ingreso, en la compu (Igna, 02/11).
 *
 * Se pide apenas se monta: lo primero que ve quien llega a la compu es el
 * código. Lo escanea con la cámara del celular, donde ya tiene el hub abierto,
 * aprueba ahí, y esta pantalla —que pregunta cada 2 segundos— recibe el
 * `token_hash` y se lo pasa a `onListo`. Vale 2 minutos: después se ofrece
 * otro, sin pedirlo solo, para no quedar generando códigos en una pestaña
 * olvidada. El dibujo del QR se hace acá, sin servicios externos. */
const CADA_MS = 2000;

const IngresoQr = ({ onListo, deshabilitado }) => {
  const [svg, setSvg] = useState("");
  const [estado, setEstado] = useState("pidiendo"); // pidiendo | esperando | vencido | rechazado | error
  const reloj = useRef(null);
  const fin = useRef(0);
  const vivo = useRef(true);
  // En un ref: si `onListo` entrara en las dependencias, cada render del
  // ingreso pediría un código nuevo.
  const alListo = useRef(onListo);
  alListo.current = onListo;

  const parar = () => {
    clearTimeout(reloj.current);
    reloj.current = null;
  };

  const preguntar = useCallback(
    async (token, nonce) => {
      if (!vivo.current) return;
      if (Date.now() > fin.current) {
        setEstado("vencido");
        setSvg("");
        return;
      }
      try {
        const r = await qrEstado(token, nonce);
        if (!vivo.current) return;
        if (r?.estado === "listo" && r.token_hash) {
          alListo.current(r.token_hash);
          return;
        }
        if (r?.estado === "esperando") {
          reloj.current = setTimeout(() => preguntar(token, nonce), CADA_MS);
          return;
        }
        setEstado(r?.estado === "rechazado" ? "rechazado" : "vencido");
        setSvg("");
      } catch {
        // Un corte de red no mata el código: se vuelve a preguntar.
        reloj.current = setTimeout(() => preguntar(token, nonce), CADA_MS * 2);
      }
    },
    [],
  );

  const pedir = useCallback(async () => {
    parar();
    setEstado("pidiendo");
    setSvg("");
    try {
      const { token, nonce, segundos } = await qrNuevo();
      const dibujo = await QRCode.toString(linkDelQr(token), {
        type: "svg",
        margin: 1,
        errorCorrectionLevel: "M",
      });
      if (!vivo.current) return;
      fin.current = Date.now() + (segundos || 120) * 1000;
      setSvg(dibujo);
      setEstado("esperando");
      reloj.current = setTimeout(() => preguntar(token, nonce), CADA_MS);
    } catch {
      if (vivo.current) setEstado("error");
    }
  }, [preguntar]);

  useEffect(() => {
    vivo.current = true;
    pedir();
    return () => {
      vivo.current = false;
      parar();
    };
  }, [pedir]);

  const otra = (
    <button type="button" className="fi-link" onClick={pedir} disabled={deshabilitado}>
      Mostrar otro código
    </button>
  );

  return (
    <div className="fi-qr">
      <div className="fi-qr__caja" aria-busy={estado === "pidiendo"}>
        {svg ? (
          <div
            className="fi-qr__img"
            role="img"
            aria-label="Código QR para entrar"
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        ) : (
          <div className="fi-qr__vacio">
            {estado === "pidiendo" && <span className="fi-spin fi-spin--oscuro" />}
            {estado === "vencido" && "El código venció."}
            {estado === "rechazado" && "Se rechazó desde el celular."}
            {estado === "error" && "No se pudo mostrar el código."}
          </div>
        )}
      </div>
      <div className="fi-qr__texto">
        <p>
          Escaneá el código con la cámara del celular donde ya tenés el hub
          abierto, y confirmá ahí.
        </p>
        {estado !== "esperando" && estado !== "pidiendo" && otra}
      </div>
    </div>
  );
};

export default IngresoQr;
